import React, { FormEvent, useEffect, useState } from 'react';
import api from '../../../newApi/apiNet';
import { useNavigate, useParams } from 'react-router-dom';
import { IUserBasicWithRoles } from '../../../interfaces/IUserBasicWithRoles';
import { IRole } from '../../../interfaces/IRole';
import Button from '../../../components/buttons/Button';
import { ButtonSize, ButtonType, ButtonVariant } from '../../../types/button';
import { useNotification } from '../../../providers/NotificationProvider';
import { NotificationStatus } from '../../../components/notification/Notification';
import UtilsHelper from '../../../helpers/utilsHelper.ts';
import AccountLayout from '../../../components/layouts/AccountLayout.tsx';
import AccountSettingsHeader from '../../../components/account/settings/AccountSettingsHeader.tsx';

export const AccountManagementRoleUserPage = () => {
    const [user, setUser] = useState<IUserBasicWithRoles | null | undefined>(undefined);
    const [availableRoles, setAvailableRoles] = useState<IRole[]>([]);
    const { user_id } = useParams();
    const _navigate = useNavigate();
    const _notificationService = useNotification();

    const handleRoleCheckedChange = (role: IRole) => {
        UtilsHelper.changeWindowDocumentTitle('Edycja uprawnień');
        const isRoleExist = user?.roles.findIndex(x => x.id === role.id);
        let updatedRoles: IRole[] = [];
        if (user) {
            if (isRoleExist !== -1) {
                updatedRoles = user.roles.filter(x => x.id !== role.id);
            } else {
                updatedRoles = [...user.roles, role];
            }
            const updatedUser: IUserBasicWithRoles = {
                id: user.id,
                email: user.email,
                customerId: user.customerId,
                roles: updatedRoles,
            };
            setUser(updatedUser);
        }
    };

    const handleSubmitEvent = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (user) {
            try {
                await api.updateUserRoles(user.id, { roles: user.roles.map(role => role.id) });
                _notificationService.showNotification('Powodzenie', 'Aktualizacja uprawnień powiodła się.', NotificationStatus.success);
            } catch (error) {
                _notificationService.showNotification('Niepowodzenie', 'Wystąpił błąd podczas aktualizacji uprawnień.', NotificationStatus.failure);
            }
        }
    };

    useEffect(() => {
        if (user_id) {
            (async () => {
                try {
                    const result = await api.getUserWithRoles(user_id);
                    setUser(result.data.user);
                    setAvailableRoles(result.data.availableRoles);
                } catch (error) {
                    _navigate('/ustawienia/uprawnienia/');
                    _notificationService.showNotification('Żądanie niepowiodło się', 'Żądanie nie powiodło się, spróbuj ponownie.', NotificationStatus.failure);
                }
            })();
        }
    }, [_navigate, _notificationService, user_id]);


    return (
        <AccountLayout>
            {user &&
                <div className="max-w-xl">
                    <AccountSettingsHeader title={'Zarządzanie uprawnieniami'} backButtonIsVisible={true} />
                    <div className="h-1 w-24 bg-sky-500 my-4 rounded-r-full" />
                    <h2 className="mt-5 text-xl font-medium">Użytkownik:</h2>
                    <form action="/update-user-roles" onSubmit={(e) => handleSubmitEvent(e)} method="post">
                        <div className="grid grid-cols-11 mt-3 bg-neutral-900 rounded-2xl p-4 py-5 leading-8">
                            <p className="font-bold col-span-2">Id:</p>
                            <p className="col-span-9">{user.id}</p>
                            {user.email &&
                                <>
                                    <p className="font-bold col-span-2">Email:</p>
                                    <p className="col-span-9">{user.email}</p>
                                </>
                            }
                            {user.customerId &&
                                <>
                                    <p className="font-bold col-span-2">Customer ID:</p>
                                    <p className="col-span-9">{user.customerId}</p>
                                </>
                            }
                        </div>
                        <p className="mt-5 text-xl font-medium">Wybierz grupy uprawnień:</p>
                        <div className="bg-neutral-900 mt-3 rounded-2xl overflow-hidden">
                            {user?.roles && availableRoles && availableRoles?.map((role, index) =>
                                <label
                                    key={role.id}
                                    htmlFor={role.id}
                                    className={`relative flex items-center select-none border-neutral-700 py-3 px-4 ${index < availableRoles.length - 1 ? 'border-b' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        name={role.id}
                                        id={role.id}
                                        checked={user?.roles.findIndex(x => x.id === role.id) !== -1}
                                        onChange={() => handleRoleCheckedChange(role)}
                                        className={'relative appearance-none peer rounded-full overflow-hidden w-5 h-5 outline-hidden ring-transparent ring-2 ring-offset-2 border border-neutral-100 ring-offset-neutral-900 checked:bg-[#008DD2] checked:border-0'}
                                    />
                                    <span
                                        className={'ml-2 text-sm text-neutral-200 font-medium mt-0.5'}>{role.name}</span>
                                    <svg
                                        className="absolute w-4 h-4 ml-0.5 mt-0.5 hidden peer-checked:block text-neutral-100"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                </label>,
                            )}
                        </div>
                        <Button
                            type={ButtonType.SUBMIT}
                            size={ButtonSize.BASE}
                            variant={ButtonVariant.PRIMARY}
                            className="mt-4 ring-offset-neutral-800"
                        >Aktualizuj uprawnienia</Button>
                    </form>
                </div>}
        </AccountLayout>
    );
};